import React from 'react';
import styled from 'styled-components';

import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';

import InputWithRifm from '../inputFormWithRifm';

import dateRecipe from '../rifmRecipes/date';

// tslint:disable-next-line:variable-name
const SDatePickerWrap = styled.div`
  display: flex;
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  flex-direction: column;
  @media only screen and (min-width: 800px) {
    flex-basis: 0;
  }
  .flatpickr-input[readonly] {
    display: none;
  }
  .flatpickr-calendar.inline {
    z-index: 1;
    margin-top: 75px;
    position: absolute;
  }
`;

export interface ICalendarProps {
  onChange: (value: string | undefined, name: string) => void;
  setIsDateValid?: (value: boolean) => void;
  maxDate?: Date;
  name: string;
  value: string;
  className?: string;
  placeholder?: string;
  isDateValid?: boolean;
  activeStartDate?: string;
  disabled?: boolean;
  label?: string;
  dataTestId?: string;
}

type CalendarState = {
  open: boolean;
  date: string;
  isDateValid: boolean;
  dateValid: string;
  dateForPicker: string;
};

// tslint:disable-next-line:variable-name
export const StyledFlatpickr = styled(({ date, handleDateChosen }) => (
  <Flatpickr
    options={{
      inline: true, dateFormat: 'm/d/Y', maxDate: new Date(), minDate: '01/01/1900',
    }}
    value={date}
    onChange={handleDateChosen}
  />
))`
  &.flatpickr-calendar.inline {
    border: 'none';
  }
`;

export default class DatePicker extends React.Component<ICalendarProps, CalendarState> {
  public node = React.createRef<HTMLDivElement>();

  constructor(props: ICalendarProps) {
    super(props);

    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  public state: CalendarState = {
    date: this.props.value,
    isDateValid: this.props.isDateValid || false,
    open: false,
    dateValid: '',
    dateForPicker: this.props.value || '01/01/1990',
  };

  public componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  private handleClickOutside(event: MouseEvent) {
    if (this.node.current) {
      if (!(this.node.current).contains(event.target as Node)) {
        this.setState(() => ({ open: false }));
      }
    }
  }

  public toggleCalendar = () => {
    this.setState({ open: !this.state.open });
  };

  public handleOnKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // added option for accessibillity
    if (event.key === 'Escape') {
      this.toggleCalendar();
    }
  };

  private checkMinMaxDate = (dateChosen: string) => {
    const currentDate = new Date(dateChosen);
    const today = new Date();
    const minDate = new Date('01/01/1900');
    today.setHours(0, 0, 0, 0);
    if (currentDate >= today || currentDate <= minDate) { return true; } return false;
  };

  private validateDate = (date: string) => {
    // If a user type invalid date / future date
    if (!/^(0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])[\/\-]\d{4}$/i.test(date) || date.length !== 10 || this.checkMinMaxDate(date)) {
      return false;
    }
    return true;
  };

  private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // If a user type more then allowed chars don't do anything
    if (value.length <= 10) {
      const dateIsValid = this.validateDate(value);
      if (dateIsValid) {
        // Adjust the output to the format needed
        const formattedDate = `${value.substring(6, 10)}-${value.substring(0, 2)}-${value.substring(3, 5)}`;
        this.props.onChange(formattedDate, this.props.name);
        this.setState(() => ({
          date: value, dateValid: '', isDateValid: true, dateForPicker: value,
        }));
        if (this.props.setIsDateValid) {
          this.props.setIsDateValid(true);
        }
      } else {
        this.props.onChange('', this.props.name);
        this.setState(() => ({
          date: value, dateValid: 'Please enter a valid date', open: false, dateForPicker: '01/01/1990', isDateValid: false,
        }));
        if (this.props.setIsDateValid) {
          this.props.setIsDateValid(false);
        }
      }
    }
  };

  public handleDateChosen = (d: any) => {
    const tmpDate = new Date(d[0].getTime());
    const month = (tmpDate.getMonth() + 1) < 10 ? `0${tmpDate.getMonth() + 1}` : (tmpDate.getMonth() + 1);
    const dayInMonth = (tmpDate.getDate()) < 10 ? `0${tmpDate.getDate()}` : (tmpDate.getDate());
    const date = `${month}/${dayInMonth}/${tmpDate.getFullYear()}`;

    this.props.onChange(`${tmpDate.getFullYear()}-${month}-${dayInMonth}`, this.props.name);

    this.setState(() => ({
      date, open: false, dateForPicker: date, dateValid: '', isDateValid: true,
    }));

    if (this.props.setIsDateValid) {
      this.props.setIsDateValid(this.state.isDateValid);
    }
  };

  public handleOnClick = () => {
    if (!this.state.open) {
      this.toggleCalendar();
    }

    // Populate the data on a first user click with the date already set from a previous application
    if (this.props.value !== '' && this.state.date === '') {
      this.setState(() => ({ date: this.props.value, dateForPicker: this.props.value }));
    }
  };

  public render() {
    const { label, value, placeholder, dataTestId } = this.props;
    const {
      date, open, dateValid, dateForPicker,
    } = this.state;
    return (
      <SDatePickerWrap data-at="dateDiv" className={this.props.className} ref={this.node} onClick={this.handleOnClick} onKeyUp={this.handleOnKeyUp}>
        <InputWithRifm
          dataAt="dateInput"
          dataTestId={dataTestId}
          name={this.props.name || 'datePicker'}
          value={date}
          label={label || ''}
          onChange={this.handleInputChange}
          placeholder={value || placeholder || 'Date of Birth'}
          rifm={dateRecipe}
          error={dateValid}
        />
        {
          open
          && (
          <StyledFlatpickr
            date={dateForPicker}
            data-at="calendarInput"
            className="{calendarClassNames}"
            handleDateChosen={this.handleDateChosen}
          />
          )
        }
      </SDatePickerWrap>

    );
  }
}
